import logger from './logger';
import remove from './remove';

// TODO: move this to a separate library

type MemoryCacheInstance<CachedType> = {
	cache: Map<string, CachedType>;
	/**
	 * Add `value` to cache with the given `key`.
	 */
	add(key: string, value: CachedType): void;
	/**
	 * Return `value` by `key`. `null` if `key` was not added to cache.
	 */
	get(key: string): CachedType | null;
	/**
	 * Remove the given `key` from the cache.
	 */
	remove(key: string): void;
	/**
	 * Check if cache has the given `key`.
	 */
	has(key: string): boolean;
	/**
	 * Remove **all** entries from cache.
	 */
	clear(): void;
};

/**
 * Simple memory cache implementation.
 */
function MemoryCache<CachedType>(args?: {
	maxEntries: number;
}): MemoryCacheInstance<CachedType> {
	const { maxEntries = 30 } = args ?? {};
	/**
	 * `order` and `cache` are used to implement an LRU (least recently used) caching approach.
	 * We keep the most recently used keys at the end of the `order` array.
	 * When the cache reaches its `maxEntries` capacity, the `order` array identifies the
	 * keys to be removed to give space to the new entries.
	 */
	const order: string[] = [];
	const cache = new Map<string, CachedType>();

	const memoryCache: MemoryCacheInstance<CachedType> = {
		cache,
		/**
		 * Add `value` to cache with the given `key`.
		 */
		add(key: string, value: CachedType): void {
			if (key == null) {
				return;
			}

			while (order.length >= maxEntries) {
				const disposableKey = order.shift()!;
				logger.debug(
					'[MemoryCache]',
					`Disposing ${disposableKey} (current size: ${cache.size})`,
				);
				cache.delete(disposableKey);
			}

			logger.debug('[MemoryCache]', `Adding ${key}`);

			cache.set(key, value);
			order.push(key);
		},
		/**
		 * Return `value` by `key`. `null` if `key` was not added to cache.
		 */
		get(key: string): CachedType | null {
			if (!cache.has(key)) {
				return null;
			}

			logger.debug('[MemoryCache]', `Retrieving ${key} from cache`);

			// keeping LRU at the end
			remove(order, (item) => item === key);
			order.push(key);

			return cache.get(key)!;
		},
		/**
		 * Remove the given `key` from the cache.
		 */
		remove(key: string): void {
			logger.debug('[MemoryCache]', `Removing ${key} from cache`);

			remove(order, (item) => item === key);
			cache.delete(key);
		},
		/**
		 * Check if cache has the given `key`.
		 */
		has(key: string): boolean {
			return cache.has(key);
		},
		/**
		 * Remove **all** entries from cache.
		 */
		clear(): void {
			cache.clear();
		},
	};

	return memoryCache;
}

export default MemoryCache;
